import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material'
import { Hub as HubIcon } from '@mui/icons-material'
import {
  AdminIntegration,
  useGetAdminIntegrationsQuery,
  useTestAdminIntegrationMutation,
  useUpdateAdminIntegrationMutation,
} from '@store/api/adminApi'
import { useAdminPermissions } from '@hooks/useAdminPermissions'
import AdminPageGate from '@components/auth/AdminPageGate'

const STATUS_OPTIONS = ['active', 'inactive', 'error'] as const

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'active') return 'success'
  if (status === 'error') return 'error'
  if (status === 'inactive') return 'warning'
  return 'default'
}

const IntegrationsManagement: React.FC = () => {
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [editing, setEditing] = useState<AdminIntegration | null>(null)
  const [status, setStatus] = useState('')
  const [configJson, setConfigJson] = useState('')
  const [configBaseline, setConfigBaseline] = useState('')
  const [configDirty, setConfigDirty] = useState(false)

  const { can, isReady } = useAdminPermissions()
  const { data, isLoading, error } = useGetAdminIntegrationsQuery()
  const [updateIntegration, { isLoading: saving }] = useUpdateAdminIntegrationMutation()
  const [testIntegration, { isLoading: testing }] = useTestAdminIntegrationMutation()

  const integrations = data?.data ?? []

  const openEdit = (row: AdminIntegration) => {
    setEditing(row)
    setStatus(row.status)
    const serialized = JSON.stringify(row.configuration ?? {}, null, 2)
    setConfigJson(serialized)
    setConfigBaseline(serialized)
    setConfigDirty(false)
  }

  const closeEdit = () => {
    setEditing(null)
  }

  const handleSave = async () => {
    if (!editing) return
    const statusChanged = status !== editing.status
    if (!statusChanged && !configDirty) {
      setMessage({ type: 'error', text: 'No changes to save' })
      return
    }

    let configuration: Record<string, unknown> | undefined
    if (configDirty) {
      try {
        configuration = JSON.parse(configJson) as Record<string, unknown>
      } catch {
        setMessage({ type: 'error', text: 'Configuration must be valid JSON' })
        return
      }
    }

    try {
      await updateIntegration({
        id: editing.id,
        ...(statusChanged ? { status } : {}),
        ...(configuration !== undefined ? { configuration } : {}),
      }).unwrap()
      setMessage({ type: 'success', text: `Updated ${editing.name}` })
      closeEdit()
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string } }
      setMessage({ type: 'error', text: apiErr?.data?.message ?? 'Failed to update integration' })
    }
  }

  const handleTest = async (id: string, name: string) => {
    try {
      const result = await testIntegration(id).unwrap()
      const msg = result.data?.message ?? 'Test completed'
      setMessage({ type: 'success', text: `${name}: ${msg}` })
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string } }
      setMessage({ type: 'error', text: apiErr?.data?.message ?? `Test failed for ${name}` })
    }
  }

  if (isReady && !can('system:integrations')) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Integrations
        </Typography>
        <Alert severity="warning">
          Your role does not include the <strong>system:integrations</strong> permission.
        </Alert>
      </Box>
    )
  }

  return (
    <AdminPageGate permission="system:integrations" title="Integrations">
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <HubIcon color="primary" />
        <Typography variant="h4" component="h1">
          Integrations
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        External service connections. Sensitive fields are redacted in API responses; submit full values
        only when updating configuration.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load integrations. Requires <strong>system:integrations</strong> permission.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : integrations.length === 0 ? (
        <Alert severity="info">No integrations registered in the database.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Errors</TableCell>
                <TableCell>Last sync</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {integrations.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>
                    <Chip label={row.status} size="small" color={statusColor(row.status)} />
                  </TableCell>
                  <TableCell>{row.errorCount}</TableCell>
                  <TableCell>
                    {row.lastSync ? new Date(row.lastSync).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(row)}>
                      Configure
                    </Button>
                    <Button
                      size="small"
                      disabled={testing}
                      onClick={() => handleTest(row.id, row.name)}
                    >
                      Test
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing && (
        <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Configure {editing.name}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Redacted fields (<code>[redacted]</code>) are preserved on save. Edit JSON only when changing
            non-secret settings; status-only updates do not send configuration.
          </Alert>
          <Stack spacing={2}>
            <FormControl size="small" sx={{ maxWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Configuration (JSON)"
              value={configJson}
              onChange={(e) => {
                setConfigJson(e.target.value)
                setConfigDirty(e.target.value !== configBaseline)
              }}
              multiline
              minRows={6}
              fullWidth
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button onClick={closeEdit}>Cancel</Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Box>
    </AdminPageGate>
  )
}

export default IntegrationsManagement
